import { useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import OpenMatchLanding from "@/components/public/OpenMatchLanding";
import TournamentLanding from "@/components/public/TournamentLanding";
import EventLanding from "@/components/public/EventLanding";
import LeagueLanding from "@/components/public/LeagueLanding";
import TrainingLanding from "@/components/public/TrainingLanding";
import GiftCardLanding from "@/components/public/GiftCardLanding";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// ─── Types ────────────────────────────────────────────────────

interface EdgeFunctionResponse {
  link: {
    entity_type: string;
    title: string | null;
    description: string | null;
  };
  org: {
    name: string;
    logo_url: string | null;
    primary_color: string | null;
    slug: string;
  } | null;
  entity: Record<string, unknown>;
}

// ─── OG Meta ──────────────────────────────────────────────────

function useOgMeta(title: string, description: string, imageUrl?: string | null) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    const setMeta = (property: string, content: string) => {
      let el = document.querySelector(`meta[property="${property}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("property", property);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta("og:title", title);
    setMeta("og:description", description);
    if (imageUrl) setMeta("og:image", imageUrl);
    setMeta("og:url", window.location.href);
    setMeta("og:type", "website");

    return () => {
      document.title = prevTitle;
    };
  }, [title, description, imageUrl]);
}

// ─── Component ────────────────────────────────────────────────

export default function PublicLandingPage() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Primary: edge function (provides entity_type, org branding, entity data)
  const {
    data: landing,
    isLoading: landingLoading,
    error: landingError,
  } = useQuery({
    queryKey: ["public-landing", code],
    queryFn: async () => {
      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/get-public-landing?code=${encodeURIComponent(code!)}`,
      );
      if (!res.ok) return null;
      return res.json() as Promise<EdgeFunctionResponse>;
    },
    enabled: !!code,
  });

  const org = landing?.org ?? null;
  const ogTitle = landing?.link?.title ?? org?.name ?? "Boply";
  const ogDescription = landing?.link?.description ?? "";
  useOgMeta(ogTitle, ogDescription);

  const handleAction = useMemo(() => {
    return (actionPath: string) => {
      if (!user) {
        sessionStorage.setItem("returnTo", window.location.pathname);
        navigate("/auth");
        return;
      }
      navigate(actionPath);
    };
  }, [user, navigate]);

  if (landingLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-lg mx-auto px-4 py-8 space-y-4">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }

  if (landingError || !landing) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-2 px-4">
          <h1 className="text-2xl font-bold">Sidan hittades inte</h1>
          <p className="text-muted-foreground">
            Denna länk är ogiltig eller har upphört att gälla.
          </p>
        </div>
      </div>
    );
  }

  const brandColor = org?.primary_color ?? undefined;

  return (
    <div className="min-h-screen bg-background">
      {/* Club header */}
      <header className="border-b bg-card">
        <div className="max-w-lg mx-auto px-4 py-4 flex items-center gap-3">
          {org?.logo_url ? (
            <img
              src={org.logo_url}
              alt={org.name}
              className="h-10 max-w-[120px] object-contain"
            />
          ) : org ? (
            <div
              className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-lg"
              style={{ backgroundColor: brandColor ?? "hsl(var(--primary))" }}
            >
              {org.name.charAt(0).toUpperCase()}
            </div>
          ) : null}
          {org && <span className="font-semibold text-lg">{org.name}</span>}
        </div>
      </header>

      {/* Entity-specific content */}
      <main className="max-w-lg mx-auto px-4 py-6">
        {renderContent(
          landing.link.entity_type,
          landing.entity,
          handleAction,
          brandColor,
        )}
      </main>

      {/* Footer */}
      <footer className="border-t mt-auto">
        <div className="max-w-lg mx-auto px-4 py-4 text-center text-xs text-muted-foreground">
          Drivs av Boply
        </div>
      </footer>
    </div>
  );
}

function renderContent(
  entityType: string,
  entity: Record<string, unknown>,
  handleAction: (path: string) => void,
  brandColor?: string,
) {
  switch (entityType) {
    case "open_match":
      return (
        <OpenMatchLanding
          entity={entity}
          onAction={handleAction}
          brandColor={brandColor}
        />
      );
    case "tournament":
      return (
        <TournamentLanding
          entity={entity}
          onAction={handleAction}
          brandColor={brandColor}
        />
      );
    case "event":
      return (
        <EventLanding
          entity={entity}
          onAction={handleAction}
          brandColor={brandColor}
        />
      );
    case "league":
      return (
        <LeagueLanding
          entity={entity}
          onAction={handleAction}
          brandColor={brandColor}
        />
      );
    case "training":
      return (
        <TrainingLanding
          entity={entity}
          onAction={handleAction}
          brandColor={brandColor}
        />
      );
    case "gift_card":
      return (
        <GiftCardLanding
          entity={entity}
          onAction={handleAction}
          brandColor={brandColor}
        />
      );
    default:
      return (
        <div className="text-center py-12 text-muted-foreground">
          Okänd typ: {entityType}
        </div>
      );
  }
}
